import { Badge, Checkbox } from '@mantine/core';
import {
  IconCalendar,
  IconCalendarEvent,
  IconChecklist,
  IconMessage,
  IconPencil,
  IconReceipt,
  IconTools,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { RowAction, RowActions } from 'src/Components/Shared/RowActions';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';
import { getNextTaskStatus, TaskRelatedEntityDto, TaskStatus } from 'src/types/task.types';

import styles from './TaskDataTable.module.scss';
import { Props } from './TaskDataTable.types';

const getContextIcon = (type: TaskRelatedEntityDto['type']) => {
  switch (type) {
    case 'project':
      return <IconTools size={12} />;
    case 'event':
      return <IconCalendarEvent size={12} />;
    case 'expense':
      return <IconReceipt size={12} />;
    case 'checklist':
      return <IconChecklist size={12} />;
    case 'communityFeedItem':
      return <IconMessage size={12} />;
    default:
      return null;
  }
};

const TaskDataTable = ({
  tasks,
  fetching,
  onTaskClick,
  onEditClick,
  onStatusChange,
  onDelete,
  canDelete = true,
  onPermissionDenied,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsMobile(1050);
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(tasks);

  const getStatusBadgeColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO:
        return 'orange';
      case TaskStatus.IN_PROGRESS:
        return 'blue';
      case TaskStatus.DONE:
        return 'teal';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO:
        return t('tasks.status.todo', 'À faire');
      case TaskStatus.IN_PROGRESS:
        return t('tasks.status.inProgress', 'En cours');
      case TaskStatus.DONE:
        return t('tasks.status.done', 'Terminé');
      default:
        return '';
    }
  };

  return (
    <DataTable
      withRowBorders={false}
      style={{ width: '100%', minHeight: '10rem' }}
      records={sortedRecords}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      noRecordsText={t('tasks.list.empty', 'Aucune tâche pour le moment')}
      fetching={fetching}
      onRowClick={({ record }) => onTaskClick(record)}
      columns={[
        {
          accessor: 'title',
          title: t('tasks.table.title', 'Titre'),
          sortable: true,
          render: task => {
            const isCompleted = task.status === TaskStatus.DONE;
            return (
              <div className={styles.titleCell}>
                <Checkbox
                  checked={isCompleted}
                  size="md"
                  onClick={e => {
                    e.stopPropagation();
                    onStatusChange?.(task.id, isCompleted ? TaskStatus.TODO : TaskStatus.DONE);
                  }}
                  readOnly
                />
                <span className={`${styles.title} ${isCompleted ? styles.completed : ''}`}>
                  {task.title}
                </span>
              </div>
            );
          },
        },
        {
          accessor: 'dueDate',
          title: t('tasks.table.dueDate', 'Échéance'),
          sortable: true,
          hidden: isTablet,
          render: task => {
            if (!task.dueDate) return <span className={styles.value}>-</span>;

            const isCompleted = task.status === TaskStatus.DONE;
            const isOverdue = dayjs(task.dueDate).isBefore(dayjs(), 'day') && !isCompleted;
            const isDueSoon =
              dayjs(task.dueDate).diff(dayjs(), 'day') <= 1 &&
              dayjs(task.dueDate).isAfter(dayjs(), 'day');

            return (
              <div
                className={`${styles.dueDate} ${isOverdue ? styles.overdue : ''} ${isDueSoon ? styles.dueSoon : ''}`}
              >
                <IconCalendar size={14} />
                <span>{dayjs(task.dueDate).format('DD/MM/YYYY')}</span>
              </div>
            );
          },
        },
        {
          accessor: 'status',
          title: t('tasks.table.status', 'Statut'),
          sortable: true,
          hidden: isMobile,
          render: task => (
            <Badge
              size="sm"
              color={getStatusBadgeColor(task.status)}
              style={{ cursor: 'pointer' }}
              onClick={e => {
                e.stopPropagation();
                onStatusChange?.(task.id, getNextTaskStatus(task.status));
              }}
            >
              {getStatusLabel(task.status)}
            </Badge>
          ),
        },
        {
          accessor: 'relatedEntities',
          title: t('tasks.table.relatedEntities', 'Éléments liés'),
          hidden: isTablet,
          render: task =>
            task.relatedEntities && task.relatedEntities.length > 0 ? (
              <div className={styles.contextBadges}>
                {task.relatedEntities.map(entity => (
                  <Badge
                    key={`${entity.type}-${entity.id}`}
                    size="xs"
                    variant="light"
                    color="violet"
                    leftSection={getContextIcon(entity.type)}
                  >
                    {entity.title}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className={styles.value}>-</span>
            ),
        },
        {
          accessor: 'assignedTo',
          title: t('tasks.table.assignedTo', 'Assigné'),
          hidden: isTablet,
          render: task =>
            task.assignedTo ? (
              <UserAvatar user={task.assignedTo} size="sm" />
            ) : (
              <span className={styles.value}>-</span>
            ),
        },
        {
          accessor: 'actions',
          title: '',
          width: 90,
          hidden: isMobile,
          render: task => {
            const actions: RowAction[] = [
              {
                key: 'edit',
                label: t('common.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                onClick: () => (onEditClick ?? onTaskClick)(task),
              },
              {
                key: 'delete',
                label: t('common.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                hidden: !onDelete,
                onClick: canDelete ? () => onDelete?.(task.id) : () => onPermissionDenied?.(),
                confirm: canDelete
                  ? {
                      title: t('tasks.delete.confirmTitle', 'Supprimer cette tâche ?'),
                      message: t(
                        'tasks.delete.confirmMessage',
                        'Voulez-vous vraiment supprimer « {{title}} » ? Cette action est irréversible.',
                        { title: task.title }
                      ),
                    }
                  : undefined,
              },
            ];

            return <RowActions actions={actions} />;
          },
          textAlign: 'right',
        },
      ]}
    />
  );
};

export default TaskDataTable;
